/**
 * Documents imprimés d'une consultation : ordonnance, demande d'examens,
 * compte rendu, fiches vierges.
 *
 * Fonctions pures : elles reçoivent le dossier renvoyé par
 * GET consultations/{id}/dossier et renvoient les options de imprimer().
 */
import { e, blocInfos, dateFr } from '../impression'

const SEXE = { M: 'Masculin', F: 'Féminin', Masculin: 'Masculin', Feminin: 'Féminin', Féminin: 'Féminin' }

function poidsRecent(d) {
  const c = (d.constantes || []).find(x => x.poids != null)
  return c ? `${String(c.poids).replace('.', ',')} kg` : ''
}

function blocPatient(d, { avecAllergies = false } = {}) {
  const p = d.patient || {}
  const c = d.consultation || {}
  return blocInfos([
    ['Patient', `${p.prenom || ''} ${p.nom || ''}`.trim()],
    ['N° dossier', p.numeroDossier || '—'],
    ['Âge', p.age || '—'],
    ['Sexe', SEXE[p.genre] || p.genre || '—'],
    ['Poids', poidsRecent(d)],
    ['Consultation', c.numero || '—'],
    ['Assurance', p.assurance ? `${p.assurance.nom || ''}${p.assurance.numero ? ' — n° ' + p.assurance.numero : ''}` : ''],
    ['Allergies', avecAllergies && p.allergies ? `<span class="anormal">${e(p.allergies)}</span>` : '', true],
  ])
}

function signatureMedecin(d) {
  const m = d.consultation?.medecin?.nom || ''
  return { droite: `<strong>Le médecin</strong>${m ? '<br>' + e(m) : ''}<br><span class="muted">Signature et cachet</span>` }
}

function posologie(l) {
  return [l.frequence, l.posologie, l.duree ? `pendant ${l.duree}` : ''].filter(Boolean).join(' — ')
}

export function documentOrdonnance(d) {
  const lignes = d.ordonnance?.lignes || []
  const corps = `
    ${blocPatient(d, { avecAllergies: true })}
    <div class="section">Prescription</div>
    <table class="lignes">
      <thead><tr><th style="width:4%">#</th><th>Médicament</th><th style="width:12%" class="centre">Qté</th><th>Posologie</th></tr></thead>
      <tbody>
        ${lignes.map((l, i) => `<tr>
          <td class="centre">${i + 1}</td>
          <td><strong>${e(l.medicament)}</strong>${l.conditionnement ? `<br><span class="muted">${e(l.conditionnement)}</span>` : ''}</td>
          <td class="centre">${e(l.quantite ?? '')}</td>
          <td>${e(posologie(l))}${l.instructions ? `<br><em>${e(l.instructions)}</em>` : ''}</td>
        </tr>`).join('')}
      </tbody>
    </table>
    ${d.consultation?.conduiteATenir ? `<div class="encadre texte"><strong>Conseils :</strong> ${e(d.consultation.conduiteATenir)}</div>` : ''}
    ${d.consultation?.prochainRdv ? `<p>Prochain rendez-vous : <strong>${dateFr(d.consultation.prochainRdv)}</strong></p>` : ''}`
  return {
    titre: 'Ordonnance médicale',
    numero: d.ordonnance?.numero || '',
    date: dateFr(d.ordonnance?.date || new Date()),
    sousTitre: d.consultation?.medecin?.nom || '',
    corps,
    signature: signatureMedecin(d),
    format: 'A5',
  }
}

export function documentDemandeExamens(d, { seulement = null } = {}) {
  const examens = (d.examens || []).filter(x => x.statut !== 'ANNULE' && (!seulement || seulement.includes(x.id)))
  const diag = (d.consultation?.diagnostics || []).map(x => x.libelle).join(' ; ')
  const corps = `
    ${blocPatient(d)}
    ${diag ? `<div class="section">Renseignements cliniques</div><div class="texte">${e(diag)}</div>` : ''}
    <div class="section">Examens demandés</div>
    <table class="lignes">
      <thead><tr><th style="width:4%">#</th><th>Examen</th><th>Précisions</th><th style="width:12%" class="centre">Urgent</th></tr></thead>
      <tbody>
        ${examens.map((x, i) => `<tr>
          <td class="centre">${i + 1}</td><td><strong>${e(x.type)}</strong></td><td>${e(x.precision || '')}</td>
          <td class="centre">${x.urgent ? '<span class="anormal">OUI</span>' : ''}</td></tr>`).join('')}
      </tbody>
    </table>`
  return {
    titre: "Demande d'examens",
    numero: d.consultation?.numero || '',
    date: dateFr(new Date()),
    sousTitre: d.consultation?.medecin?.nom || '',
    corps,
    signature: signatureMedecin(d),
  }
}

function constantesTexte(cv) {
  if (!cv) return ''
  return [
    cv.tension ? `TA ${cv.tension} mmHg` : '', cv.pouls ? `Pouls ${cv.pouls}/min` : '',
    cv.temperature != null ? `T° ${String(cv.temperature).replace('.', ',')} °C` : '', cv.saturation != null ? `SpO₂ ${cv.saturation} %` : '',
    cv.frequenceRespiratoire ? `FR ${cv.frequenceRespiratoire}/min` : '', cv.poids != null ? `Poids ${String(cv.poids).replace('.', ',')} kg` : '',
    cv.glycemie != null ? `Glycémie ${String(cv.glycemie).replace('.', ',')} g/L` : '',
  ].filter(Boolean).join(' · ')
}

export function documentCompteRendu(d) {
  const c = d.consultation || {}
  const cv = (d.constantes || []).find(x => x.consultationId === c.id) || null
  const bloc = (titre, texte) => texte ? `<div class="section">${e(titre)}</div><div class="texte">${e(texte)}</div>` : ''
  const lignes = d.ordonnance?.lignes || []
  const examens = (d.examens || []).filter(x => x.statut !== 'ANNULE')
  const corps = `
    ${blocPatient(d, { avecAllergies: true })}
    ${bloc('Motif de consultation', c.motif)}
    ${bloc('Histoire de la maladie', c.histoireMaladie)}
    ${d.patient?.antecedents ? bloc('Antécédents', d.patient.antecedents) : ''}
    ${cv ? `<div class="section">Constantes</div><div class="texte">${e(constantesTexte(cv))}</div>` : ''}
    ${bloc('Examen clinique', c.examenClinique)}
    ${(c.diagnostics || []).length ? `<div class="section">Diagnostic${c.diagnostics.length > 1 ? 's' : ''}</div>
      <ul>${c.diagnostics.map(x => `<li>${x.principal ? '<strong>' : ''}${e(x.libelle)}${x.code ? ` <span class="muted">(${e(x.code)})</span>` : ''}${x.principal ? '</strong>' : ''}</li>`).join('')}</ul>` : ''}
    ${examens.length ? `<div class="section">Examens complémentaires</div>
      <table class="lignes"><thead><tr><th>Examen</th><th>Résultat</th></tr></thead><tbody>
      ${examens.map(x => `<tr><td>${e(x.type)}</td><td>${x.resultat || x.conclusion ? e([x.resultat, x.conclusion].filter(Boolean).join(' — ')) : '<span class="muted">En attente</span>'}</td></tr>`).join('')}
      </tbody></table>` : ''}
    ${lignes.length ? `<div class="section">Traitement prescrit</div>
      <ul>${lignes.map(l => `<li><strong>${e(l.medicament)}</strong>${l.quantite ? ` × ${e(l.quantite)}` : ''} — ${e(posologie(l))}</li>`).join('')}</ul>` : ''}
    ${bloc('Conduite à tenir', c.conduiteATenir)}
    ${c.orientation ? `<div class="section">Orientation</div><div class="texte">${e((d.orientations || {})[c.orientation] || c.orientation)}${c.prochainRdv ? ` — contrôle le ${dateFr(c.prochainRdv)}` : ''}</div>` : ''}
    ${(d.complements || []).length ? `<div class="section">Compléments</div>
      ${d.complements.map(k => `<div class="texte"><span class="muted">${dateFr(k.date, true)} — ${e(k.auteur?.nom || '')}</span><br>${e(k.texte)}</div>`).join('')}` : ''}`
  return {
    titre: 'Compte rendu de consultation',
    numero: c.numero || '',
    date: dateFr(c.date || new Date()),
    sousTitre: c.medecin?.nom || '',
    corps,
    signature: signatureMedecin(d),
  }
}

/** Fiche vierge à remplir à la main (panne, consultation hors écran). */
export function documentFicheVierge(d, type) {
  const ligne = '<div style="border-bottom:1px dashed #999;height:24px"></div>'
  const zones = type === 'ordonnance'
    ? [['Traitement', 8], ['Posologie / instructions', 2]]
    : [['Examens demandés', 5], ['Renseignements cliniques', 2]]
  return {
    titre: type === 'ordonnance' ? 'Ordonnance médicale' : "Demande d'examens",
    date: dateFr(new Date()),
    sousTitre: d.consultation?.medecin?.nom || '',
    corps: `${blocPatient(d, { avecAllergies: type === 'ordonnance' })}
      ${zones.map(([l, n]) => `<div class="section">${e(l)}</div>${ligne.repeat(n)}`).join('')}`,
    signature: signatureMedecin(d),
    format: type === 'ordonnance' ? 'A5' : 'A4',
  }
}
